import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";
import {
  getInitialDataRequest,
  placeBidRequest,
} from "../../api/controller/AuctionController";
import { handleResponse } from "../../custom/helpers/HandleResponse";
import notify from "../../custom/helpers/Notify";
import echo from "../../api/core/echo";
import { useRecoilValue } from "recoil";
import { themeTypeState } from "../../state/GlobalState";

// Custom shadow styles
const styles = `
  .shadow-success-custom {
    box-shadow: 0 0 0.3rem rgba(40, 167, 69, 0.3) !important;
  }
  .shadow-danger-custom {
    box-shadow: 0 0 0.3rem rgba(220, 53, 69, 0.3) !important;
  }
  .shadow-secondary-custom {
    box-shadow: 0 0 0.3rem rgba(108, 117, 125, 0.3) !important;
  }
`;

const Home = () => {
  const dataRef = useRef(null);
  const [data, _setData] = useState(null);
  const setData = (data) => {
    dataRef.current = data;
    _setData(data);
  };
  const [showModal, setShowModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState({});
  const [bidAmounts, setBidAmounts] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const themeType = useRecoilValue(themeTypeState);

  // Inject custom styles
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const getSlotStatus = (startTime, endTime) => {
    const now = new Date();
    const start = new Date(startTime);
    const end = new Date(endTime);

    if (now < start) return "upcoming";
    if (now > end) return "expired";
    return "ongoing";
  };

  const getSlotStyle = (status, item) => {
    switch (status) {
      case "ongoing":
        return "shadow-sm shadow-success-custom";
      case "upcoming":
        return "shadow-sm shadow-secondary-custom";
      case "expired":
        // Check if the user won this slot
        const myBid = data?.myBids?.find((bid) => bid.slot_id === item.id);
        const isWinner =
          myBid &&
          item.currentHighestBid &&
          myBid.amount >= item.currentHighestBid;
        if (isWinner) {
          return "shadow-sm shadow-success-custom border border-success border-2";
        } else if (myBid) {
          return "shadow-sm shadow-danger-custom";
        } else {
          return "shadow-sm shadow-secondary-custom";
        }
      default:
        return "";
    }
  };

  const getExpiredMessage = (item) => {
    const myBid = data?.myBids?.find((bid) => bid.slot_id === item.id);
    const isWinner =
      myBid && item.currentHighestBid && myBid.amount >= item.currentHighestBid;

    if (isWinner) {
      return (
        <div className="pb-3 text-center fw-bold fs-4 d-flex align-items-center justify-content-center gap-2">
          <i
            className="fas fa-trophy text-warning"
            style={{ fontSize: "1.5rem" }}
          ></i>
          <span className="text-info">You won!</span>
        </div>
      );
    } else if (myBid) {
      return (
        <div className="text-center text-danger fw-bold fs-4 mb-2">
          <i className="fas fa-frown me-2"></i>
          Unsuccessful Bid
        </div>
      );
    } else {
      return (
        <div className="text-center text-secondary fw-bold fs-4 mb-2">
          <i className="fas fa-clock me-2"></i>
          Expired
        </div>
      );
    }
  };

  const sortSlotsByName = (slots) => {
    return [...slots].sort((a, b) => {
      const numA = parseInt(a.name.replace("P", ""));
      const numB = parseInt(b.name.replace("P", ""));
      return numA - numB;
    });
  };

  useEffect(() => {
    (async () => {
      const response = await getInitialDataRequest();
      setData(response);
    })();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    console.log(data);
  }, [data]);

  useEffect(() => {
    echo.channel(`GuestEvent`).listen(".BiddingEvents", (incomingData) => {
      console.log("incomingData", incomingData);
      console.log("data", dataRef.current);
      if (incomingData.type === "Deleted") {
        const deleteId = parseInt(incomingData.data);
        setData({
          ...dataRef.current,
          data: (dataRef.current.data || []).filter(
            (item) => parseInt(item.id) !== deleteId
          ),
        });
      } else {
        const currentData = dataRef.current || { data: [] };
        const incomingId = parseInt(incomingData.data.id);
        const idx = (currentData.data || []).findIndex(
          (item) => parseInt(item.id) === incomingId
        );
        if (idx === -1) {
          setData({
            ...dataRef.current,
            data: [
              ...(dataRef.current.data || []),
              { ...incomingData.data, id: incomingId },
            ],
          });
        } else {
          setData({
            ...dataRef.current,
            data: (dataRef.current.data || []).map((item) =>
              parseInt(item.id) === incomingId
                ? { ...item, ...incomingData.data, id: incomingId }
                : item
            ),
          });
        }
      }
    });
    return () => {
      echo.leave(`GuestEvent`);
    };
    // eslint-disable-next-line
  }, []);

  const closeModal = () => {
    setShowModal(false);
    setSelectedSlot(null);
    setBidAmounts({});
  };

  const handleBidAmountChange = (slotId, value) => {
    setBidAmounts((prev) => ({
      ...prev,
      [slotId]: value,
    }));
  };

  const handlePlaceBid = async (slotId, amount) => {
    setLoadingSlots((prev) => ({ ...prev, [slotId]: true }));
    if (!amount || isNaN(amount)) {
      notify({ status: "error", message: "Please enter a valid bid amount." });
      setLoadingSlots((prev) => ({ ...prev, [slotId]: false }));
      return;
    }
    const response = await placeBidRequest({
      id: slotId,
      Amount: Number(amount),
    });
    handleResponse(response);
    if (response.Status === "success") {
      setData({
        ...dataRef.current,
        myBids: [
          ...(dataRef.current.myBids || []).filter(
            (bid) => bid.slot_id !== slotId
          ),
          { slot_id: slotId, amount: Number(amount) },
        ],
      });
      setBidAmounts((prev) => ({
        ...prev,
        [slotId]: "",
      }));
    }
    setLoadingSlots((prev) => ({ ...prev, [slotId]: false }));
  };

  const getBiddingStatus = () => {
    const now = new Date();
    const items = data?.data || [];

    const hasInProgress = items.some(
      (i) =>
        new Date(i.bid_start_time) <= now && now <= new Date(i.bid_end_time)
    );
    const hasUpcoming = items.some((i) => new Date(i.bid_start_time) > now);
    const hasExpired = items.some((i) => new Date(i.bid_end_time) < now);

    if (hasInProgress)
      return <span className="text-success">🔴 Bidding is in progress</span>;
    if (hasUpcoming)
      return <span className="text-warning">🕒 Awaiting bidding start</span>;
    if (hasExpired)
      return <span className="text-info">✅ Bidding has ended</span>;
    return <span>-</span>;
  };

  const getCountdown = (endTime) => {
    const end = new Date(endTime);
    const diff = end - currentTime;
    
    if (diff <= 0) return { hours: 0, minutes: 0, seconds: 0 };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return { hours, minutes, seconds };
  };

  const getBiddingHeader = () => {
    const now = new Date();
    const items = data?.data || [];

    const hasInProgress = items.some(
      (i) =>
        new Date(i.bid_start_time) <= now && now <= new Date(i.bid_end_time)
    );
    const hasUpcoming = items.some((i) => new Date(i.bid_start_time) > now);
    const hasExpired = items.some((i) => new Date(i.bid_end_time) < now);

    if (hasInProgress)
      return (
        <div className="text-center bg-success rounded-4 p-2">
          <span className="text-white fw-bold fs-4">IN PROGRESS</span>
        </div>
      );
    if (hasUpcoming)
      return (
        <div className="text-center bg-warning rounded-4 p-2">
          <span className="text-white fw-bold fs-4">AWAITING START</span>
        </div>
      );
    if (hasExpired)
      return (
        <div className="text-center bg-info rounded-4 p-2">
          <span className="text-white fw-bold fs-4">COMPLETED</span>
        </div>
      );
    return <span>-</span>;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className=" mb-4">
          <div className="d-flex align-items-center gap-4">
            <div style={{ width: "25%" }}>{getBiddingHeader()}</div>
            <div className="flex-grow-1">
              <div className="p-3">{getBiddingStatus()}</div>
            </div>
          </div>
        </div>
        <div className="mb-5">
          <Row className="g-3">
            {data &&
              sortSlotsByName(data.data).map((item) => {
                const status = getSlotStatus(
                  item.bid_start_time,
                  item.bid_end_time
                );
                const slotStyle = getSlotStyle(status, item);
                return (
                  <Col
                    className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3"
                    key={item.id}
                  >
                    <div
                      className={`p-3 text-center  rounded-3 position-relative ${
                        themeType === "dark" ? " text-light" : " text-dark"
                      } ${slotStyle}`}
                      // style={{ height: "245px" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          {status === "ongoing" && (
                            <div className="bg-primary text-white rounded-3 px-2 py-1" style={{ 
                              fontSize: '0.75rem',
                              fontFamily: 'monospace',
                              minWidth: '60px',
                              textAlign: 'center'
                            }}>
                              {(() => {
                                const countdown = getCountdown(item.bid_end_time);
                                return `${countdown.hours.toString().padStart(2, '0')}:${countdown.minutes.toString().padStart(2, '0')}:${countdown.seconds.toString().padStart(2, '0')}`;
                              })()}
                            </div>
                          )}
                        </div>
                        <Button
                          className="btn-sm"
                          onClick={() => {
                            setSelectedSlot(item);
                            setShowModal(true);
                          }}
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                      </div>
                      <div className="text-center">
                                                 <div
                           style={{
                             height: "6rem",
                             display: "flex",
                             alignItems: "center",
                             justifyContent: "center",
                             overflow: "hidden",
                           }}
                         >
                           <span
                             className="fw-bold fs-3 "
                             style={{
                               display: "-webkit-box",
                               WebkitLineClamp: 3,
                               WebkitBoxOrient: "vertical",
                               overflow: "hidden",
                               textOverflow: "ellipsis",
                               lineHeight: "1.8rem",
                               color: themeType === "dark" ? "#EBEBEB" : "#474747"
                             }}
                           >
                            {item?.name}
                          </span>
                        </div>
                      </div>
                      {status === "expired" && getExpiredMessage(item)}
                      {status === "upcoming" ? (
                        <>
                                                     <div className="bg-secondary bg-opacity-10 rounded-2 py-2 px-3 mt-2">
                             <div className="text-start">
                               <div
                                 className={`fs-6 ${
                                   themeType === "dark"
                                     ? "text-info"
                                     : "text-secondary"
                                 }`}
                               >
                                 Starting Time
                               </div>
                               <div
                                 className={`fw-bold fs-5 ${
                                   themeType === "dark"
                                     ? "text-info"
                                     : "text-secondary"
                                 }`}
                               >
                                 {new Date(
                                   item.bid_start_time
                                 ).toLocaleDateString("en-US", {
                                   day: "numeric",
                                   month: "short",
                                   year: "numeric",
                                 })}
                                 <br />
                                 {new Date(
                                   item.bid_start_time
                                 ).toLocaleTimeString("en-US", {
                                   hour: "2-digit",
                                   minute: "2-digit",
                                   hour12: false,
                                 })}
                                 <br />
                                 <span
                                   style={{
                                     fontSize: '0.75rem',
                                     fontFamily: 'monospace',
                                     color: '#6c757d'
                                   }}
                                 >
                                   {(() => {
                                     const now = new Date();
                                     const startTime = new Date(item.bid_start_time);
                                     const diff = startTime - now;
                                     
                                     if (diff <= 0) return "Started";
                                     
                                     const hours = Math.floor(diff / (1000 * 60 * 60));
                                     const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                                     const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                                     
                                     return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                                   })()}
                                 </span>
                               </div>
                             </div>
                           </div>
                          <div className="bg-secondary bg-opacity-10 rounded-2 py-2 px-3 mt-2">
                            <div className="text-start">
                              <div
                                className={`fs-6 ${
                                  themeType === "dark"
                                    ? "text-info"
                                    : "text-secondary"
                                }`}
                              >
                                Starting Price
                              </div>
                              <div
                                className={`fw-bold fs-4 ${
                                  themeType === "dark"
                                    ? "text-info"
                                    : "text-secondary"
                                }`}
                              >
                                {item.start_price?.toLocaleString() || 0}
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {(() => {
                            const myBid = data?.myBids?.find(
                              (bid) => bid.slot_id === item.id
                            );
                            const isWinner =
                              myBid &&
                              item.currentHighestBid &&
                              myBid.amount >= item.currentHighestBid;

                            return (
                              <>
                                {!isWinner && (
                                  <div className="bg-info bg-opacity-10 rounded-2 py-2 px-3 mt-2">
                                    <div className="text-start">
                                      <div
                                        className={`fs-6 ${
                                          themeType === "dark"
                                            ? "text-info"
                                            : "text-primary"
                                        }`}
                                      >
                                        Highest Bid
                                      </div>
                                      <div
                                        className={`fw-bold fs-4 ${
                                          themeType === "dark"
                                            ? "text-info"
                                            : "text-primary"
                                        }`}
                                      >
                                        {item.currentHighestBid?.toLocaleString() ||
                                          0}
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <div className="bg-success bg-opacity-10 rounded-2 py-2 px-3 mt-2">
                                  <div className="text-start">
                                    <div className="fs-6 text-success">
                                      My Bid
                                    </div>
                                    <div className="text-success fw-bold fs-4">
                                      {myBid?.amount?.toLocaleString() || 0}
                                    </div>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </>
                      )}

                      {status === "ongoing" ? (
                        <div className="mt-3 ">
                          <div className="mt-3">
                            <div className="d-flex gap-2 position-relative">
                              <Input
                                type="number"
                                placeholder="Enter bid amount"
                                onChange={(e) =>
                                  handleBidAmountChange(item.id, e.target.value)
                                }
                                value={bidAmounts[item.id] || ""}
                                onKeyUp={(e) => {
                                  if (e.key === "Enter") {
                                    handlePlaceBid(
                                      item.id,
                                      bidAmounts[item.id]
                                    );
                                  }
                                }}
                              />
                              <Button
                                color="primary"
                                onClick={() =>
                                  handlePlaceBid(item.id, bidAmounts[item.id])
                                }
                                disabled={loadingSlots[item.id]}
                              >
                                <i className="fas fa-gavel"></i>
                              </Button>
                              {loadingSlots[item.id] && (
                                <div className="position-absolute top-50 start-50 translate-middle">
                                  <div
                                    className="spinner-border spinner-border-sm text-primary"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </Col>
                );
              })}
          </Row>
        </div>
      </Container>

      <Modal isOpen={showModal} toggle={closeModal}>
        <ModalHeader toggle={closeModal}>{selectedSlot?.name}</ModalHeader>
        <ModalBody>
          <img
            src={process.env.REACT_APP_API_PUBLIC_URL + selectedSlot?.image}
            alt="Slot"
            style={{ width: "100%", marginBottom: 10 }}
          />
          {selectedSlot?.description && (
            <Card className="m-0 border-1">
              <CardBody>
                <p className="mb-0">{selectedSlot.description}</p>
              </CardBody>
            </Card>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Home;
